import { useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import useCreateOrganization, { type Organization } from "./useCreateOrganization";
import Icon from "app/components/shared/Icon";
import Button from "app/components/shared/Button";
import Spinner from "app/components/shared/Spinner";

export const Container = styled.div`
  width: 100%;
  padding-top: 3em;
  padding-bottom: 2em;
`;

export const Progress = styled.div`
  margin-bottom: 1em;
  font-size: 0.85em;
  color: var(--charcoal-300);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
`;

export const Title = styled.h3`
  margin-top: 0;
  font-size: 1.5em;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
`;

const AnimatedIcon = styled(Icon)`
  @keyframes check {
    0% {
      transform: scale(0.2);
    }
    100% {
      transform: scale(1);
    }
  }

  color: var(--lime-500);
  animation: check 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const Input = styled.input`
  font-size: 1em !important;

  &[aria-invalid="true"] {
    border-color: red;
  }
`;

const ErrorMessage = styled.span`
  color: red;
  display: block;
  margin-bottom: 0.5em;
`;

interface Fields {
  name: string;
}

interface Props {
  user: {
    name: string;
  };
  onSuccess: (organization: Organization) => void;
}

export default function Organization({ onSuccess, user: { name } }: Props) {
  const { createOrganization, loading, error, data } = useCreateOrganization();

  const {
    formState: { errors, isSubmitSuccessful, isSubmitted, isValid },
    register,
    handleSubmit,
    setError,
  } = useForm<Fields>({});

  useEffect(() => {
    if (error) {
      if (data?.error) {
        setError("name", { type: "custom", message: data.error });
      } else {
        setError("name", {
          type: "custom",
          message: "Something went wrong. Please try again.",
        });
      }
    }

    if (data?.organization) {
      onSuccess(data.organization);
    }
  }, [error, data]);

  return (
    <form
      className="mt-8"
      onSubmit={handleSubmit((data: Fields) => {
        createOrganization(data);
      })}
    >
      <Header>
        <Title className="mb-2">Create organization</Title>
      </Header>
      <div className="mb-4">
        <p style={{ marginBottom: "1em" }} className="max-w-prose text-charcoal-600">
          A Buildkite organization is the central place for your pipelines, test suites, teams and
          dashboards.
        </p>
        <label htmlFor="organization" className="sr-only">
          Organization name
        </label>
        <Input
          id="organization"
          autoComplete="off"
          className="input"
          autoFocus={true}
          placeholder={name ? `e.g. ${name}'s Company` : "e.g. My Company"}
          type="text"
          aria-invalid={isSubmitted && !!errors.name}
          {...register("name", { required: true })}
        />
        {errors.name?.message && <ErrorMessage>{errors.name?.message}</ErrorMessage>}
      </div>
      <ButtonGroup>
        <Button type="submit" theme="onboarding" disabled={!isValid && !loading}>
          Continue
        </Button>
        {loading && <Spinner size="1.5em" color={false} />}
        {!loading && isSubmitSuccessful && <AnimatedIcon icon="check-large" />}
      </ButtonGroup>
    </form>
  );
}
